import { MATRIX_CHANNEL } from "@/constants";
import queryString from "query-string";

const getSessionStorage = (key: string) => {
  return (
    window.sessionStorage.getItem(key) &&
    decodeURIComponent((window as any).sessionStorage.getItem(key))
  );
};

export const getChannel = () => {
  const params = queryString.parse(window.location.search) as any;
  return (
    (params.channel && decodeURIComponent(params.channel)) ||
    getSessionStorage("channel") ||
    ""
  );
};

/**
 * 动态创建script标签
 * @param url
 * @param callback
 */
export const loadScript = (url: string, callback: () => void) => {
  const eleScript: any = document.createElement("script");
  eleScript.type = "text/javascript";
  eleScript.src = url;
  document.getElementsByTagName("head")[0].appendChild(eleScript);

  if (eleScript.readyState) {
    eleScript.onreadystatechange = function () {
      if (
        eleScript.readyState === "loaded" ||
        eleScript.readyState === "complete"
      ) {
        eleScript.onreadystatechange = null;
        callback();
      }
    };
  } else {
    eleScript.onload = function () {
      callback();
    };
  }
};

export const getMatrixChannel = () => {
  const params: any = queryString.parse(window.location.search);
  return (
    params.matrix_channel ||
    window.sessionStorage.getItem("matrix_channel") ||
    MATRIX_CHANNEL
  );
};

export const is = (val: unknown, type: string) => {
  return toString.call(val) === `[object ${type}]`;
};

export const isNumber = (val: unknown): val is number => {
  return is(val, "Number");
};

export function getCookies(cookies: string) {
  try {
    if (!cookies) return {};
    const list = cookies.split(";");
    const params: {
      [key: string]: string;
    } = {};
    for (let i = 0; i < list.length; i++) {
      const [key, value] = list[i].split("=");
      params[key.trim()] = decodeURIComponent(value);
    }
    return params;
  } catch (e: any) {
    console.log("e.message====>", e.message);
    return {};
  }
}

export const isMobile = () => {
  if (typeof window === "undefined") return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

export const sleep = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

// 判断gpt_content 是否为json字符串
export const isJsonStr = (str: string) => {
  if (typeof str === "string") {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  }
  return false;
};

export const isSafari = () => {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

// 判断是否来自华为PC预装定制版内嵌
export const isFromHuaweiCustom = () => {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent.toLowerCase();

  return ua.includes("channel:zhyshop");
};

let supportWebp: boolean | undefined = undefined;

export const isSupportWebp = () => {
  if (typeof window === "undefined") return false;
  if (supportWebp === void 0) {
    supportWebp =
      document
        .createElement("canvas")
        .toDataURL("image/webp")
        .indexOf("data:image/webp") === 0
  }
  return supportWebp;
};

export const thumbnail = (url: string, width?: number, quality = 80) => {
  if (/\.svg$/.test(url) || /\.(webp)$/.test(url)) return url;

  const params = queryString.parseUrl(url);

  // imageView2 加上这个后，会导致不校验 token
  // 暂时不处理 token 地址
  if (params?.query?.token) return url;

  // 加签参数需要保留
  const signString =
    params?.query?.k && params?.query?.t
      ? `&k=${params.query.k}&t=${params.query.t}`
      : "";

  // 分割参数，转 webp 将会被丢弃
  const uri = url.split("?");
  // 分割缩略图 https://material-center-pre.meitudata.com/material/image/6462f5d0f05f84631.png!thumb-w240
  const realUrl = uri[0].split("!");

  const currentUrl = `${realUrl[0]}?imageView2/2${width ? "/w/" + width : ""}`;
  if (isSupportWebp()) {
    return `${currentUrl}/format/webp/q/${quality}/ignore-error/1${signString}`;
  }
  return `${currentUrl}/q/${quality}/ignore-error/1${signString}`;
};

export const isAndroid = () => {
  if (typeof window === "undefined") return false;
  return /Android/i.test(navigator.userAgent);
};

export const isQQ = () => {
  if (typeof window === "undefined") return false;
  return /QQ/i.test(navigator.userAgent);
};

export const isWeiXin = () => {
  if (typeof window === "undefined") return false;
  // 微信浏览器 或者 企业微信浏览器
  return (
    /MicroMessenger/i.test(navigator.userAgent) ||
    /wxwork/i.test(navigator.userAgent)
  );
};

const workerScript = `
    onmessage = function(event) {
        const { timeout } = event.data;

        setTimeout(() => {
            postMessage('done');
        }, timeout);
    };
`;
export const workerSleep = (timeout = 1000) => {
  return new Promise<void>((resolve) => {
    const blob = new Blob([workerScript], {
      type: "application/javascript",
    });
    const worker = new Worker(URL.createObjectURL(blob));
    worker.onmessage = function () {
      resolve();
      worker.terminate(); // 结束工作者线程
    };
    worker.postMessage({ timeout });
  });
};

export const getJSONByUrl = async (url: string) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open("GET", url, true);
    xhr.responseType = "json";

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response);
      } else {
        reject(new Error(`请求失败，状态码：${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      reject(new Error("网络错误"));
    };

    xhr.send();
  });
};

export const isIOS = () => {
  if (typeof window === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

export const filterParams = (params: any) => {
  let paramsStr = "?";
  for (const key in params) {
    paramsStr += `${key}=${key === "tool_url" ? encodeURIComponent(params[key]) : params[key]
      }&`;
  }
  return paramsStr.slice(0, -1);
};

let clientInfo: any = undefined;

export const getClientInfo = async () => {
  if (!clientInfo && typeof window !== "undefined") {
    clientInfo = await import("@meitu/pc-bridge").then((res) => res.clientInfo);
  }
  return clientInfo;
};

export const isPcPlatform = async () => {
  if (!clientInfo && typeof window !== "undefined") {
    clientInfo = await getClientInfo();
  }
  if (clientInfo?.isClient) {
    return true;
  }

  return false;
};

export const getOsType = async () => {
  let osType = "web";
  if ((await getClientInfo()).isClient) {
    osType = (await getClientInfo()).isMac ? "macos" : "windows";
  }
  return osType;
};

export const getSource = async () => {
  const clientInfo = await getClientInfo();
  let source = "";
  if (clientInfo.isMac || clientInfo.isMacAppStore) {
    source = "MTXX-MAC";
  } else if (clientInfo.isPC) {
    source = "MTXX-PC";
  } else if (clientInfo.isLinux) {
    source = "MTXX-LINUX";
  } else {
    source = "MTXX-WEB";
  }
  return source;
};
